<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useDisplay, useRules } from 'vuetify'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useRoute } from 'vuetify/lib/composables/router.mjs'
import LoginEmailStep from './LoginEmailStep.vue'
import LoginMethodsStep from './LoginMethodsStep.vue'
import FEmailLogin from '@/components/feature/FEmailLogin/FEmailLogin.vue'
import getEnvironment from '../../utils/env.ts'
import {
  authProviders,
  useAuth,
  OAUTH_QUERY_PARAMS,
  type AuthProvider,
  type OAuthParams,
} from '@/composables/useAuth.ts'
import { waitForPeerSessionHydration } from '@/composables/useAuthTabSync'
import { useLoginMethods, type LoginMethodsView } from './useLoginMethods'
import { REMEMBER_ME_DEFAULT, useRememberedLogin } from './useRememberedLogin'
import mdiAlertCircle from '~icons/mdi/alert-circle'

type LoginStep = 'email' | 'methods' | 'otp'

const PRIVACY_POLICY_URL = 'https://www.amili.se/integritetspolicy'
const COOKIE_POLICY_URL = 'https://www.amili.se/cookie-policy'

const display = useDisplay()
const { t } = useI18n()
const rules = useRules()
const env = getEnvironment()
const auth = useAuth()
const router = useRouter()
const route = useRoute()
const { lookup, loading: lookupLoading, errorKey: lookupErrorKey } = useLoginMethods()
const { rememberedEmail, rememberMe, sessionParams, commit, forget } = useRememberedLogin()

const step = ref<LoginStep>('email')
const email = ref('')
const methodsView = ref<LoginMethodsView | null>(null)
// Only the heading renders until the mount-time decision is made: a sibling tab may still deliver
// its session, a deep link may autostart, and a remembered address opens straight in step 2.
const bootstrapping = ref(true)
const emailLoginError = ref<string | null>(null)
const signInError = ref<string | null>(null)

const redirectUrl = computed(() =>
  typeof router.currentRoute.value.query['return-to'] === 'string'
    ? decodeURIComponent(router.currentRoute.value.query['return-to'])
    : undefined
)

function extractOAuthParamsFromQuery() {
  const query = router.currentRoute.value.query

  if (query.auth_errors != null) return {}

  const oauth = typeof query.oauth === 'string' ? query.oauth : null
  const loginHint = typeof query.login_hint === 'string' ? query.login_hint : undefined
  const userId = typeof query.user_id === 'string' ? query.user_id : undefined
  const sessionTimeout = typeof query.session_timeout === 'string' ? parseInt(query.session_timeout, 10) : undefined
  const endSessionOnClose = typeof query.end_session_on_close === 'string' ? query.end_session_on_close : undefined

  const cleanedQuery = Object.fromEntries(
    Object.entries(query).filter(([key]) => !OAUTH_QUERY_PARAMS.includes(key as (typeof OAUTH_QUERY_PARAMS)[number]))
  )

  if (Object.keys(cleanedQuery).length !== Object.keys(query).length) {
    void router.replace({ query: cleanedQuery })
  }

  // Only a provider with an OAuth start URL can be autostarted; any other value (the one-time code
  // has none) falls through to the normal flow.
  const redirectProvider = authProviders.find((p) => p.id === oauth && 'href' in p)

  return {
    oauth: redirectProvider?.id ?? null,
    loginHint,
    userId,
    sessionTimeout,
    endSessionOnClose,
  }
}

// Immediate: a session that arrived between the route guard's decision and this setup is sent on too.
watch(
  auth.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      if (typeof router.currentRoute.value.query['return-to'] === 'string') {
        void router.replace(decodeURIComponent(router.currentRoute.value.query['return-to']))
      } else {
        void router.replace({ name: 'Dashboard' })
      }
    }
  },
  { immediate: true }
)

const authErrors = computed(() => {
  if (typeof route.value?.query.auth_errors === 'string') {
    try {
      const decoded = decodeURIComponent(route.value.query.auth_errors)
      const parsed = JSON.parse(decoded)
      if (!Array.isArray(parsed)) return null
      return parsed as string[]
    } catch {
      return null
    }
  }
  return null
})

const displayedErrors = computed<string[] | null>(() => {
  const errors: string[] = []
  if (authErrors.value != null) errors.push(...authErrors.value)
  if (lookupErrorKey.value != null) errors.push(lookupErrorKey.value)
  if (emailLoginError.value != null) errors.push(emailLoginError.value)
  if (signInError.value != null) errors.push(signInError.value)
  return errors.length > 0 ? errors : null
})

const errorTitle = computed(() => {
  if (authErrors.value != null && authErrors.value.length > 0) return t('common.login_failed')
  if (emailLoginError.value != null) return t('common.code_delivery_failed_title')
  return undefined
})

function clearErrors() {
  const query = { ...router.currentRoute.value.query }
  delete query.auth_errors
  void router.replace({ query })
  lookupErrorKey.value = null
  emailLoginError.value = null
  signInError.value = null
}

// Same rule as the step-1 field, so a malformed deep-link address stays in step 1 without a lookup.
function isValidEmail(value: string): boolean {
  const rule = rules.email()
  return typeof rule === 'function' ? rule(value) === true : rule === true
}

async function runLookup() {
  const view = await lookup(email.value)
  // A technical error keeps step 1; the alert says what went wrong and the button allows a retry.
  if (view == null) return
  methodsView.value = view
  step.value = 'methods'
}

// Every OAuth round starts here so a start that fails before the browser leaves (e.g. no secure
// context for the PKCE challenge) shows up in the alert instead of being a dropped promise.
async function startSignIn(provider: AuthProvider, params: OAuthParams): Promise<boolean> {
  signInError.value = null
  try {
    await auth.signInWithRedirect(provider, params)
    return true
  } catch (err) {
    console.error('Starting the sign-in failed:', err)
    signInError.value = 'common.login_failed'
    return false
  }
}

function selectProvider(provider: AuthProvider) {
  // The box decides both where the login is stored and whether the address is kept for next time.
  commit(email.value)
  if (provider === 'email') {
    step.value = 'otp'
    return
  }
  void startSignIn(provider, {
    returnUrl: redirectUrl.value,
    loginHint: email.value,
    ...sessionParams.value,
  })
}

// Fallback from the code screen for Visma.net tenants without a user document; the session choice from step 2 still applies.
function signInWithVismaNet(loginHint: string) {
  void startSignIn('visma_net', {
    returnUrl: redirectUrl.value,
    loginHint,
    ...sessionParams.value,
  })
}

function forgetAndRestart() {
  forget()
  rememberMe.value = REMEMBER_ME_DEFAULT
  email.value = ''
  methodsView.value = null
  step.value = 'email'
}

function backToMethods() {
  emailLoginError.value = null
  step.value = 'methods'
}

onMounted(async () => {
  const { oauth, ...params } = extractOAuthParamsFromQuery()

  // A sibling tab may still be delivering its session: nothing starts before it has had the chance
  // to answer. If it does, the watch on isAuthenticated redirects and no new login is started.
  await waitForPeerSessionHydration()
  if (auth.isAuthenticated.value) return

  // A started round leaves the page with the indicator up; a failed start continues into the normal flow.
  if (oauth != null && (await startSignIn(oauth, { returnUrl: redirectUrl.value, ...params }))) return

  if (params.loginHint != null) {
    email.value = params.loginHint
  } else if (rememberedEmail.value != null) {
    email.value = rememberedEmail.value
    // The device is already trusted; the box opens checked on this path only.
    rememberMe.value = true
  }

  if (email.value !== '' && isValidEmail(email.value)) await runLookup()
  bootstrapping.value = false
})
</script>

<template>
  <v-row
    class="fill-height login-bg"
    no-gutters
  >
    <v-col v-if="display.mdAndUp.value" />
    <v-col
      :style="{ 'margin-bottom': '10vh' }"
      class="pa-6 align-self-end"
    >
      <div class="d-flex align-center ga-4">
        <img
          alt="amili"
          height="24"
          src="/src/assets/logo_black.png"
        />

        <v-chip
          v-if="env != null && env.id !== 'production'"
          class="text-uppercase"
          :color="env.color ?? undefined"
          size="small"
          variant="tonal"
          >{{ env.name }}</v-chip
        >
      </div>

      <div style="max-width: 616px">
        <h1 class="text-display-large mt-10 mb-6 font-weight-bold">
          {{ step === 'otp' ? t('common.sign_in_with_otp') : t('common.sign_in') }}
        </h1>

        <v-alert
          v-if="displayedErrors != null"
          type="error"
          variant="tonal"
          class="mb-6"
          :icon="mdiAlertCircle"
          closable
          :title="errorTitle"
          @click:close="clearErrors()"
        >
          {{ displayedErrors.map((error) => t(error)).join(', ') }}
        </v-alert>

        <v-progress-circular
          v-if="bootstrapping"
          indeterminate
          color="primary"
          :aria-label="t('common.loading')"
        />
        <login-email-step
          v-else-if="step === 'email'"
          v-model="email"
          :loading="lookupLoading"
          @submit="runLookup"
        />
        <login-methods-step
          v-else-if="step === 'methods' && methodsView != null"
          v-model:remember-me="rememberMe"
          :email="email"
          :view="methodsView"
          @edit-email="step = 'email'"
          @not-you="forgetAndRestart"
          @select="selectProvider"
        />
        <f-email-login
          v-else-if="step === 'otp'"
          :email="email"
          :session-params="sessionParams"
          @back="backToMethods"
          @visma-net="signInWithVismaNet"
          @error="emailLoginError = $event"
        />
      </div>

      <footer class="d-flex ga-4 mt-16">
        <a
          :href="PRIVACY_POLICY_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="text-decoration-underline"
        >
          {{ t('common.privacy_policy') }}
        </a>
        <span aria-hidden="true">|</span>
        <a
          :href="COOKIE_POLICY_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="text-decoration-underline"
        >
          {{ t('common.cookies') }}
        </a>
      </footer>
    </v-col>
  </v-row>
</template>

<style scoped>
.login-bg {
  background: url('/src/assets/bg.png') no-repeat top left;
  background-size: 60%;
}
</style>
